import React, { useState, useRef, useEffect } from 'react';
import { Settings, Heart, LogOut, ChevronDown, Calendar } from 'lucide-react';

const UserDropdown = ({ user, onShowDashboard, onShowMealPlans, onShowCycleDashboard, onShowCycleLogger, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleItemClick = (action) => {
    setIsOpen(false);
    action();
  };

  if (!user) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-full bg-gradient-to-r from-orange-500 to-green-500 text-white hover:from-orange-600 hover:to-green-600 transition-all transform hover:scale-105 shadow-lg"
      >
        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
        </div>
        <span className="font-medium hidden sm:block">{user.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 transform animate-in slide-in-from-top-2 duration-200">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <div>
                <p className="font-semibold text-gray-800">{user.name}</p>
                <p className="text-sm text-gray-500">@{user.username}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* User Details */}
            <button
              onClick={() => handleItemClick(onShowDashboard)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-gray-700 hover:text-orange-600"
            >
              <Settings className="w-5 h-5" />
              <div>
                <p className="font-medium">User Details</p>
                <p className="text-sm text-gray-500">Manage your profile and settings</p>
              </div>
            </button>

            {/* Your Meal Plans */}
            <button
              onClick={() => handleItemClick(onShowMealPlans)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 text-gray-700 hover:text-purple-600"
            >
              <Heart className="w-5 h-5" />
              <div>
                <p className="font-medium">Your Meal Plans</p>
                <p className="text-sm text-gray-500">View and manage saved meal plans</p>
              </div>
            </button>

            {/* Cycle Dashboard & Logger - Only for Female users */}
            {user.sex === 'Female' && (onShowCycleDashboard || onShowCycleLogger) && (
              <>
                <div className="border-t border-gray-100 my-2"></div>
                
                {/* Cycle Dashboard */}
                {onShowCycleDashboard && (
                  <button
                    onClick={() => handleItemClick(onShowCycleDashboard)}
                    className="w-full px-4 py-3 text-left hover:bg-purple-50 transition-colors flex items-center space-x-3 text-gray-700 hover:text-purple-600"
                  >
                    <Calendar className="w-5 h-5" />
                    <div>
                      <p className="font-medium">Cycle Dashboard</p>
                      <p className="text-sm text-gray-500">Track your menstrual cycle</p>
                    </div>
                  </button>
                )}
                
                {/* Cycle Journal */}
                {onShowCycleLogger && (
                  <button
                    onClick={() => handleItemClick(onShowCycleLogger)}
                    className="w-full px-4 py-3 text-left hover:bg-pink-50 transition-colors flex items-center space-x-3 text-gray-700 hover:text-pink-600"
                  >
                    <Heart className="w-5 h-5" />
                    <div>
                      <p className="font-medium">Cycle Journal</p>
                      <p className="text-sm text-gray-500">Log symptoms & cravings</p>
                    </div>
                  </button>
                )}
              </>
            )}

            {/* Divider */}
            <div className="border-t border-gray-100 my-2"></div>

            {/* Logout */}
            <button
              onClick={() => handleItemClick(onLogout)}
              className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors flex items-center space-x-3 text-gray-700 hover:text-red-600"
            >
              <LogOut className="w-5 h-5" />
              <div>
                <p className="font-medium">Logout</p>
                <p className="text-sm text-gray-500">Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;